import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Publish as ClockIcon } from '@vgno/icons';

import {
  formatAssetUpcomingDate,
  getAssetDisplayTitle,
  getAssetMainImage,
  getAssetMetadata,
  getAssetPromoUrl,
  isAssetUpcoming,
  isCurrentlyLive,
  isEntertainment,
  isLiveStream,
  isUpcomingLive,
} from '../../../shared/helpers/assetHelpers';
import { useServerTime } from '../../utils/useServerTime';
import { getImageSources } from '../../../helpers/images';
import { PaywallSplashButtons } from '../PaywallSplashButtons/PaywallSplashButtons';
import { Splash } from '../Splash/Splash';
import { getAssetLabels } from '../AssetSplash/getAssetLabels';
import { config } from '../../../config';
import { usePaywallState } from '../../utils/usePaywallState';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { FC } from 'react';

interface PaywalledAssetSplashProps {
  asset: SvpAssetAdditional;
}

const PaywalledAssetSplash: FC<PaywalledAssetSplashProps> = ({ asset }) => {
  const { serverTime } = useServerTime();
  const { t } = useTranslation();
  const {
    paywallState: { hasAccess },
  } = usePaywallState(asset);

  const image = getAssetMainImage(asset);
  const title = getAssetDisplayTitle(asset);
  const description = asset.description;
  const previewUrl = getAssetPromoUrl(asset);
  const isLive = isCurrentlyLive(asset, serverTime);
  const verticalImage = getAssetMetadata(asset).verticalPosterImage;

  const verticalImageSources = useMemo(
    () =>
      getImageSources({
        imageUrl: verticalImage,
        aspectRatio: 'vertical',
        imagePreset: 'assetsSliderVertical',
      }),
    [verticalImage]
  );

  const { seasonNumber, episodeNumber } = asset.series ?? {};

  let subtitle;
  if (asset.series) {
    subtitle = `${t('Season')} ${seasonNumber}, ${t('Episode')} ${episodeNumber}`;
  } else if (
    (isLiveStream(asset) && isUpcomingLive(asset, serverTime)) ||
    (!isLiveStream(asset) && isAssetUpcoming(asset))
  ) {
    subtitle = (
      <>
        <ClockIcon /> {formatAssetUpcomingDate(asset)}
      </>
    );
  }

  const buttonsHelperText = useMemo(() => {
    if (config.provider === 'ab' && !hasAccess && isEntertainment(asset)) {
      return '19 kr/mån första tre månaderna. Därefter halva priset året ut. (ord pris 149 kr/mån). Ingen bindningstid';
    }
  }, [hasAccess, asset]);

  const labels = useMemo(() => getAssetLabels(asset), [asset]);

  return (
    <Splash
      imageUrl={image}
      title={title}
      description={description}
      buttons={<PaywallSplashButtons asset={asset} />}
      buttonsHelperText={buttonsHelperText}
      subtitle={subtitle}
      previewUrl={previewUrl}
      badge={undefined}
      verticalImageSources={
        !isLive && verticalImage ? verticalImageSources : undefined
      }
      labels={labels}
    />
  );
};

export { PaywalledAssetSplash };
